import { createContext, useContext, useRef, useState } from "react";
import { usePunditUserContext } from "@circle-react/contexts";
import { getLocalStorage, setLocalStorage } from "../utils/storageUtils";

interface SupportWidgetContextValue {
  collapseWidget: () => void;
  currentSpaceProperties: {
    name: string;
    emoji: string;
    custom_emoji_url: string;
    custom_emoji_dark_url: string;
  };
  expandWidget: () => void;
  isExpanded: boolean;
  isOpen: boolean;
  openWidget: () => void;
  setCurrentSpaceProperties: (properties: {
    name: string;
    emoji: string;
    custom_emoji_url: string;
    custom_emoji_dark_url: string;
  }) => void;
  toggleWidget: (options?: { shouldTriggerOnWidgetClose?: boolean }) => void;
}

export const SupportWidgetContextV2 = createContext<SupportWidgetContextValue>({
  isOpen: false,
  toggleWidget: () => {},
  openWidget: () => {},
  isExpanded: false,
  expandWidget: () => {},
  collapseWidget: () => {},
  currentSpaceProperties: {
    name: "",
    emoji: "",
    custom_emoji_url: "",
    custom_emoji_dark_url: "",
  },
  setCurrentSpaceProperties: () => {},
});

SupportWidgetContextV2.displayName = "SupportWidgetContextV2";

export const useSupportWidgetContextV2 = () => {
  const context = useContext(SupportWidgetContextV2);
  if (!context) {
    throw new Error(
      "useSupportWidgetContextV2 must be used within a SupportWidgetContextProviderV2",
    );
  }
  return context;
};

interface SupportWidgetProviderProps {
  children: React.ReactNode;
}

export const SupportWidgetContextProviderV2 = ({
  children,
}: SupportWidgetProviderProps) => {
  const { isViewOnlyMasquerading } = usePunditUserContext();
  const onWidgetClose = useRef<(() => void) | null>(null);

  const [isOpen, setIsOpen] = useState(() => {
    if (isViewOnlyMasquerading) return false;
    const storage = getLocalStorage();
    return storage?.isOpen ?? false;
  });

  const [isExpanded, setIsExpanded] = useState(() => {
    const storage = getLocalStorage();
    return storage?.isExpanded === true;
  });

  const [currentSpaceProperties, setCurrentSpaceProperties] = useState({
    name: "",
    emoji: "",
    custom_emoji_url: "",
    custom_emoji_dark_url: "",
  });

  const expandWidget = () => {
    const storage = getLocalStorage() || {};
    setLocalStorage({ ...storage, isExpanded: true });
    setIsExpanded(true);
  };

  const collapseWidget = () => {
    const storage = getLocalStorage() || {};
    setLocalStorage({ ...storage, isExpanded: false });
    setIsExpanded(false);
  };

  const toggleWidget = ({ shouldTriggerOnWidgetClose = false } = {}) => {
    if (isViewOnlyMasquerading) {
      return;
    }

    if (
      shouldTriggerOnWidgetClose &&
      isOpen &&
      onWidgetClose.current !== null
    ) {
      onWidgetClose.current();
    } else {
      const storage = getLocalStorage() || {};
      setLocalStorage({ ...storage, isOpen: !isOpen });
      setIsOpen(!isOpen);
    }
  };

  const openWidget = () => {
    const storage = getLocalStorage() || {};
    setLocalStorage({ ...storage, isOpen: true });
    setIsOpen(true);
  };

  return (
    <SupportWidgetContextV2.Provider
      value={{
        isOpen,
        toggleWidget,
        openWidget,
        isExpanded,
        expandWidget,
        collapseWidget,
        currentSpaceProperties,
        setCurrentSpaceProperties,
      }}
    >
      {children}
    </SupportWidgetContextV2.Provider>
  );
};
